import BaseApi, { PRIORITY } from '@lumapps/base-api';
import { ServerListRequest, ServerListResponse } from '@lumapps/base-api/types';
import { CACHE_TYPE } from '@lumapps/cache';
import { TranslatableObject } from '@lumapps/translations';
import { OneOrMany } from '@lumapps/utils/types/OneOrMany';

import {
    Content,
    LegacyContentStatus,
    type BaseContent,
    type ContentTemplate,
    type ReadModeContent,
    type WriteModeContent,
} from '../types';

// Base content view fields
const CONTENT_GET_DEFAULT_FIELDS =
    'id,slug,type,link,instance,title,isHomepage,canEdit,properties,hasCommentWidget,externalKey,startDate,endDate,lastRevision,lastPublishedAt';
// Fields required by the content analytics link the the contextual actions:
const CONTENT_ANALYTICS_FIELDS = 'customContentType,status,editors,authorId';
// User fields required on the author and writer.
const USER_FIELDS = 'id,firstName,lastName,email,apiProfile';
// Fields used in the metadata widget:
const WIDGET_META_FIELDS = `comments,liked,likes,type,writerDetails(${USER_FIELDS}),authorDetails(${USER_FIELDS}),publicationDate,customContentTypeTags,customContentTypeDetails(tags(id,name),name),canonicalUrl`;
// Content header fields
const CONTENT_HEADER_FIELDS = 'headerDetails';
// Fields used in view only
const CONTENT_VIEW_FIELDS = 'template(isV2Compatible)';
// Fields used in preview only
const CONTENT_PREVIEW_FIELDS = 'template(isV2Compatible,components),thumbnail,mediaThumbnail';
// Fields used in designer to get publication informations
export const CONTENT_LIVE_REVISION_FIELDS = 'status,startDate,endDate,lastPublishedAt,lastRevision,isHomepage';

export const contentApi = new BaseApi({ path: 'content' });
export const templateApi = new BaseApi({ path: 'template' });

/** Get content params. */
export interface GetContentAPIParams {
    /** Content identifier. */
    uid?: string;
    /** Content slug. */
    slug?: string;
    /** Instance identifier. */
    instance?: string;
    /** Fields. */
    fields?: string;
    /** The preview token used to bypass EDIT access authorization */
    previewToken?: string;
    /** Action for which to get the content (defaults to PAGE_READ) */
    action?: 'PAGE_EDIT' | 'PAGE_READ';
    /** Abort signal */
    signal?: AbortSignal;
}

/** Get content. */
export const get = ({ signal, ...params }: GetContentAPIParams, useCache = true, headers = {}) =>
    useCache
        ? contentApi.getCacheFirst('get', CACHE_TYPE.MEMORY, PRIORITY.HIGH, { params, headers, signal })
        : contentApi.get('get', { params, headers, signal });

export interface GetContentForViewOptions {
    /** Skip fetch of the content header */
    skipHeader?: boolean;
    /** Fetch for preview (slight change to the fetched fields and params) */
    forPreview?: boolean;
    /** The preview token used to bypass EDIT access authorization */
    token?: string;
    /** Abort signal */
    signal?: AbortSignal;
}

/** Get content for content view (with defaults fields). */
export const getContentForView = async (
    contentId: string,
    { signal, forPreview, token, skipHeader }: GetContentForViewOptions = {},
): Promise<ReadModeContent> => {
    // Core content fields:
    let fields = `${CONTENT_GET_DEFAULT_FIELDS},${WIDGET_META_FIELDS},${CONTENT_ANALYTICS_FIELDS}`;

    let previewToken: GetContentAPIParams['previewToken'];

    let action: GetContentAPIParams['action'];
    if (!forPreview) {
        // View use case
        fields += `,${CONTENT_VIEW_FIELDS}`;
    } else {
        // Preview use case
        fields += `,${CONTENT_PREVIEW_FIELDS}`;
        action = 'PAGE_EDIT';
        previewToken = token;
    }

    if (!skipHeader) {
        // Content header
        fields += `,${CONTENT_HEADER_FIELDS}`;
    }

    const { data } = await get({ uid: contentId, previewToken, fields, action, signal });
    return data;
};

/**
 * Get content for content edition (with defaults fields). */
export const getContentForEdition = async (
    contentId: string,
    { signal }: { signal?: AbortSignal } = {},
): Promise<WriteModeContent> => {
    const action: GetContentAPIParams['action'] = 'PAGE_EDIT';

    const data = await Promise.allSettled([
        /**
         * In edit mode, we need to get the last live revision in order to compute
         * correctly the current state of the content (status, date of publication/schedule, etc...)
         * This additional GET could be removed if the backend provides the informations about the last published version within the endpoint.
         */
        get(
            {
                action: 'PAGE_READ',
                fields: CONTENT_LIVE_REVISION_FIELDS,
                uid: contentId,
            },
            false,
        ),
        get({ uid: contentId, action, signal }, false),
    ]).then(([liveRevisionRequest, contentRequest]) => {
        let content = {};
        if (contentRequest.status === 'rejected') {
            throw contentRequest.reason;
        }
        content = contentRequest.value.data;

        // Live revision is not required.
        if (liveRevisionRequest.status === 'fulfilled') {
            content = { ...content, liveRevision: liveRevisionRequest.value.data };
        }
        return content as WriteModeContent;
    });

    return data;
};

export const getTemplate = async (templateId: string, signal?: AbortSignal) => {
    const { data: template } = await templateApi.get<ContentTemplate>('/get', {
        params: { uid: templateId },
        signal,
    });

    return template;
};
/** Delete content params. */
export interface DeleteContentAPIParams {
    uid: string[];
}

/** Delete content. */
export const remove = (params: DeleteContentAPIParams = { uid: [] }) => contentApi.delete('deleteMulti', { params });

/** Follow content. */
export const follow = (contentId: string, params: { notify?: boolean } = {}) => {
    const defaultParams = {
        notify: false,
    };

    return contentApi.post(`/${contentId}/follow`, { ...defaultParams, ...params });
};

/** Unfollow content. */
export const unfollow = (contentId: string) => contentApi.delete(`/${contentId}/unfollow`);

/** Like content. */
export const like = (contentId: string) => contentApi.post('/like', { uid: contentId });

/** Unlike content. */
export const unlike = (contentId: string) => contentApi.post('/unlike', { uid: contentId });

/**
 * List content params.
 * {@see https://api.lumapps.com/docs/output/_schemas/servercontentcontentmessagescontentlistrequest}
 */
export interface ListContentAPIParams extends ServerListRequest {
    action?: string;
    /** The lang on which the query should apply. */
    lang: OneOrMany<string>;
    /** The content types. */
    type?: OneOrMany<Content['type']>;
    /** types to exclude from search */
    excludeType?: OneOrMany<Content['type']>;
    /** The list of content IDs. */
    ids?: OneOrMany<string>;
    /** list content cursor */
    cursor?: string;
    /** list instance id */
    instanceId?: string | string[];
    /** list of statuses to filter by */
    status?: string[];
    /** list of custom content type ids to filter by */
    customContentType?: string[];
    /** list of custom content type tags ids to filter by */
    customContentTypeTags?: string[];
    /** search query */
    query?: string;
    /** customer id */
    customerId?: string;
    /** the type of the query */
    queryType?: 'getContentByIds';
    /** Author filter — email string (not id), legacy API convention */
    author?: string;
    /** Feed (group) uids for the "Visible by" filter */
    feed?: string[];
    /** Combined metadata filter — array of selected metadata groups, one entry per root. */
    combinedMetadata?: Array<{ metadata: string[] }>;
    /** Expiration window start (ISO 8601) */
    endDateFrom?: string;
    /** Expiration window end (ISO 8601) */
    endDateTo?: string;
}

/** List content. */
export const list = <T extends BaseContent = ReadModeContent>(params: ListContentAPIParams) => {
    // Monolite/monolithe compatibility (weird stuff with the legacy api)
    // As monolite is stricter we make sure we send correctly serialized data to it
    // to avoid a fallback on monolith.
    // Note: Monolith knows how to handle both formats.
    if (params?.sortOrder && typeof params.sortOrder === 'string') {
        // eslint-disable-next-line no-param-reassign
        params.sortOrder = [params.sortOrder];
    }
    if (params?.instanceId && typeof params.instanceId === 'string') {
        // eslint-disable-next-line no-param-reassign
        params.instanceId = [params.instanceId];
    }
    return contentApi.post<ServerListResponse<T>>('list', params);
};

export const contentQueryKeys = {
    all: () => ['content'] as const,
    getForEdition: (contentId: string) => [...contentQueryKeys.all(), 'edit', contentId] as const,
    getForPreview: (contentId: string, token?: string) =>
        [...contentQueryKeys.all(), 'preview', contentId, { token }] as const,
    listContents: (params?: ListContentAPIParams) => [...contentQueryKeys.all(), 'list', params] as const,
    /**
     * Infinite list key with uid as a dedicated part of the query key (enables per-custom-type cache invalidation)
     * Without params, returns the 3-element prefix `['content', 'list', uid]` so that
     * `queryClient.invalidateQueries({ queryKey: contentQueryKeys.listByContentType(uid) })`
     * correctly prefix-matches all queries for that custrom-type via React Query prefix-matching,
     * regardless of the trailing params segment.
     */
    listByContentType: (contentTypeId: string, params?: ListContentAPIParams) =>
        [...contentQueryKeys.all(), 'list', contentTypeId, ...(params ? [params] : [])] as const,
    getLiveRevision: (contentId: string) => [...contentQueryKeys.all(), contentId, 'liveRevision'] as const,
    exportBody: (contentId: string) => [...contentQueryKeys.all(), 'exportBody', contentId] as const,
    getTemplate: (templateId: string) => [...contentQueryKeys.all(), 'template', templateId],
};

/** Archive content. */
export const archive = (contentId: string) =>
    contentApi.post<Content>('/archive', { uid: contentId, status: LegacyContentStatus.archive });

/** Unarchive content. */
export const unarchive = (contentId: string) =>
    contentApi.post<Content>('/unarchive', { uid: contentId, status: LegacyContentStatus.unarchive });

/** Update content status. */
export const updateStatus = ({
    contentId,
    status,
    comment,
    ignoreOptionalRules,
}: {
    contentId: string;
    status: LegacyContentStatus;
    comment?: string;
    /** When true, bypasses optional nanny rules (used for the "Publish anyway" flow). */
    ignoreOptionalRules?: boolean;
}) =>
    contentApi.post<Content>(
        '/updateStatus',
        { uid: contentId, status, comment },
        ignoreOptionalRules ? { params: { ignoreOptionalRules: true } } : undefined,
    );

export const deleteContent = (contentId: string) => contentApi.delete('/delete', { params: { uid: contentId } });

/**
 * Return the content as a textual string.
 * Use the [/content/body](https://lumapps-api.stoplight.io/docs/lumapps-public-api/ec80c2b279351-export-content-body-beta) endpoint.
 */
export const exportBody = (contentId: string) =>
    contentApi.get<{
        body: TranslatableObject;
    }>('/body', { params: { uid: contentId } });

export interface SaveContentParams {
    /** Whether to ignore optional nanny rules when saving content */
    ignoreOptionalRules?: boolean;
}

export const saveContent = (content: WriteModeContent, params?: SaveContentParams) =>
    contentApi.post<WriteModeContent>('save', content, { params });

export const setContentAsHomepage = (contentId: string) => contentApi.post('setHomepage', { uid: contentId });
